import { useTranslation } from "react-i18next";
import { ProgressBar, Tag, Tile } from "@carbon/react";
import DataTable, {
  DataTableColumn,
  DataTableRow,
} from "../../carbon/DataTable/DataTable";
import { SupplierScore } from "../../../types";
import { formatCurrency, formatPercentage } from "../utils/kpiHelpers";
import "./supplierScorecard.scss";

interface SupplierScorecardProps {
  suppliers: SupplierScore[];
  /** Active currency: only this currency's suppliers are listed (spend is never mixed across currencies). */
  currency: string;
}

export type SupplierGrade = "A" | "B" | "C";

// Composite weights (sum to 1): delivering on time matters most, then the saving achieved, then speed.
const WEIGHT_ON_TIME = 0.5;
const WEIGHT_SAVINGS = 0.3;
const WEIGHT_CYCLE = 0.2;
// A saving at/above this earns the full savings score; a cycle at/above this earns none.
const SAVINGS_FULL_PCT = 20;
const CYCLE_ZERO_DAYS = 30;
const GRADE_A_MIN = 0.75;
const GRADE_B_MIN = 0.5;

const clamp01 = (n: number) =>
  Number.isFinite(n) ? Math.min(1, Math.max(0, n)) : 0;

/**
 * Client-side A/B/C grade: a 0..1 composite of on-time % (higher better), saving % (higher
 * better) and cycle days (lower better), each clamped so one outlier cannot dominate.
 */
export const gradeSupplier = (s: SupplierScore): SupplierGrade => {
  const composite =
    WEIGHT_ON_TIME * clamp01(s.onTimePct / 100) +
    WEIGHT_SAVINGS * clamp01(s.savingsPct / SAVINGS_FULL_PCT) +
    WEIGHT_CYCLE * clamp01(1 - s.avgCycleDays / CYCLE_ZERO_DAYS);
  if (composite >= GRADE_A_MIN) return "A";
  if (composite >= GRADE_B_MIN) return "B";
  return "C";
};

const GRADE_TAG_TYPE = { A: "green", B: "blue", C: "gray" } as const;

interface ScorecardRow extends DataTableRow {
  id: string;
  name: string;
  spend: string;
  awards: number;
  savings: string;
  cycle: string;
  onTime: React.ReactNode;
  rating: React.ReactNode;
}

const SupplierScorecard = ({ suppliers, currency }: SupplierScorecardProps) => {
  const { t } = useTranslation();

  const rows: ScorecardRow[] = suppliers
    .filter((s) => s.currency === currency)
    .sort((a, b) => b.spend - a.spend)
    .map((s) => {
      const grade = gradeSupplier(s);
      return {
        id: s.companyId,
        name: s.name,
        spend: formatCurrency(s.spend, s.currency),
        awards: s.awards,
        savings: formatPercentage(s.savingsPct),
        cycle: `${s.avgCycleDays.toFixed(1)} ${t("analytics.days")}`,
        onTime: (
          <div className="supplier-scorecard__ontime">
            <ProgressBar
              size="small"
              hideLabel
              label={t("analytics.scorecard.onTime")}
              value={clamp01(s.onTimePct / 100) * 100}
              max={100}
            />
            <span className="supplier-scorecard__ontime-figure">
              {formatPercentage(s.onTimePct)}
            </span>
            <span className="supplier-scorecard__ontime-caption">
              {t("analytics.scorecard.onTimeCaption")}
            </span>
          </div>
        ),
        rating: (
          <Tag
            type={GRADE_TAG_TYPE[grade]}
            size="md"
            data-testid="supplier-grade"
          >
            {grade}
          </Tag>
        ),
      };
    });

  const headers: DataTableColumn<ScorecardRow>[] = [
    { key: "name", header: t("analytics.scorecard.supplier") },
    { key: "spend", header: t("analytics.scorecard.spend") },
    { key: "awards", header: t("analytics.scorecard.awards") },
    { key: "savings", header: t("analytics.scorecard.avgSaving") },
    { key: "cycle", header: t("analytics.scorecard.avgCycle") },
    { key: "onTime", header: t("analytics.scorecard.onTime") },
    { key: "rating", header: t("analytics.scorecard.rating") },
  ];

  return (
    <Tile className="supplier-scorecard">
      <h3 className="supplier-scorecard__title">
        {t("analytics.scorecard.title")}
      </h3>
      <DataTable
        headers={headers}
        rows={rows}
        isLoading={false}
        empty={
          <p className="supplier-scorecard__empty">
            {t("analytics.scorecard.noData")}
          </p>
        }
      />
    </Tile>
  );
};

export default SupplierScorecard;
